import React, { useState, Children, isValidElement, ReactNode, ReactElement } from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../../../app/StyleGuide';

const Container = styled.div`
  flex: 1;
  width: 100%;
  background: white;
  line-height: ${Type.Leading.tall};
  padding-bottom: ${Grid._5};
`;

const TabsBar = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  background: ${Color.Gray._05};
  box-shadow: inset 0 -1px rgba(0, 0, 0, 0.05);
  li {
    font-size: ${Type.Scale._3};
    padding: ${Grid._4} ${Grid._5};
    font-weight: ${Type.Weight.normal};
    color: ${Type.Color.medium};
    display: inline-flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    border-top: 3px solid transparent;
    cursor: pointer;
  }
  li.active-tab {
    background: white;
    font-weight: ${Type.Weight.medium};
    color: ${Type.Color.dark};
    border-top-color: ${Color.Orange._50};
    box-shadow: 1px 0 rgba(0, 0, 0, 0.05), -1px 0 rgba(0, 0, 0, 0.05);
  }
`;

const TabContent = styled.div`
  color: ${Type.Color.dark};
  max-width: 700px;
  margin: ${Grid._7} auto;
  padding: ${Grid._4};
`;

type TabProps = {
  name: string;
  children: ReactNode;
};

function Tab({ children }: TabProps) {
  return <>{children}</>;
}

type TabbedPanelProps = {
  defaultTab?: string;
  children: ReactNode;
};

export function TabbedPanel({ defaultTab, children }: TabbedPanelProps) {
  const tabs: { name: string; element: ReactElement<TabProps> }[] = [];

  Children.forEach(children, child => {
    if (isValidElement<TabProps>(child) && child.type === Tab) {
      tabs.push({ name: child.props.name, element: child });
    } else if (child != null) {
      console.warn('TabbedPanel: Only TabbedPanel.Tab children are supported. Received:', child);
    }
  });

  const [activeTab, setActiveTab] = useState(defaultTab ?? tabs[0]?.name ?? '');

  if (tabs.length === 0) return null;

  return (
    <Container>
      <TabsBar>
        {tabs.map(tab => (
          <li
            key={tab.name}
            className={tab.name === activeTab ? 'active-tab' : ''}
            onClick={() => setActiveTab(tab.name)}
          >
            {tab.name}
          </li>
        ))}
      </TabsBar>
      <TabContent>
        {tabs.map(tab => (
          <div key={tab.name} style={{ display: tab.name === activeTab ? 'block' : 'none' }}>
            {tab.element}
          </div>
        ))}
      </TabContent>
    </Container>
  );
}

TabbedPanel.Tab = Tab;
